import React from "react";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { RESOURCE_NAME } from "common/authorization";
import { SUBMISSION_CATEGORY, TASK_STATUS } from "common/constants";
import { SUBMISSION_CATEGORY_TO_LABEL } from "common/utils/submissions";
import { TASK_STATUS_OPTIONS } from "common/utils/tasks";
import {
  getListForObject,
  getTypedObject,
  isCustomMapGeometry,
  TargetObject,
  TypedObject,
} from "common-client/utils/customObject";
import {
  GetAccountActivityDocument,
  GetActivitiesDocument,
  GetSubmissionDocument,
  GetTasksDocument,
  SubmissionType,
  useCreateTaskMutation,
  useUpdateTaskMutation,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  InputRow,
  PrimaryButtons,
} from "../Common/__styles__/Modal";
import { Button } from "../Common/Button";
import Modal from "../Common/Modal";
import { Body, Title } from "../Common/Typography";
import {
  Datepicker,
  Select,
  Text,
  Textarea,
  UserSelect,
} from "../Inputs/react-hook-form";
import type { Task } from "./__queries__";
import { isAddressPanelProperty } from "./types";

const makeAssociatedRecordTypeOptions = ({
  submissionTypes,
  typedObject,
}: {
  submissionTypes: Array<
    Pick<SubmissionType, "id" | "name" | "category" | "attachments">
  >;
  typedObject?: Maybe<TypedObject>;
}) => {
  const submissionTypesForObject = getListForObject({
    list: submissionTypes,
    objectType: typedObject?.type,
    customMap: isCustomMapGeometry(typedObject?.object)
      ? { id: typedObject?.object.customMapId }
      : undefined,
  });

  type Option = { value: string; label: string };

  const groups = new Map<SUBMISSION_CATEGORY, Array<Option>>();

  submissionTypesForObject.forEach(type => {
    const group = groups.get(type.category);
    const entry: Option = { value: type.id, label: type.name };

    if (group) {
      group.push(entry);
    } else {
      groups.set(type.category, [entry]);
    }
  });

  const options: Array<{
    label: string;
    options: Array<Option>;
  }> = [];

  for (let [category, opts] of groups) {
    const label = SUBMISSION_CATEGORY_TO_LABEL[category];
    options.push({ label, options: opts });
  }

  return options;
};

export type TaskModalSubmission = {
  id: string;
  submissionTypeVersion?: {
    submissionType?: {
      id: string;
    };
  };
  property?: Maybe<TargetObject>;
};

export interface TaskModalHook {
  onSubmit: () => void;
  onCancel: () => void;
  targetObject?: Maybe<TargetObject>;
  submission?: Maybe<TaskModalSubmission>;
  existingTask?: Maybe<Task>;
}

export const useTaskModal = ({
  onSubmit,
  onCancel,
  targetObject,
  submission,
  existingTask,
}: TaskModalHook) => {
  const [showTaskModal, hideTaskModal] = useModal(
    () => (
      <Modal
        onRequestClose={() => {
          onCancel();
        }}
      >
        <TaskForm
          onSubmit={onSubmit}
          onCancel={onCancel}
          targetObject={targetObject}
          existingTask={existingTask}
          submission={submission}
        />
      </Modal>
    ),
    [onSubmit]
  );

  return [showTaskModal, hideTaskModal] as const;
};

const getLabel = (targetObject?: Maybe<TargetObject>) => {
  if (!targetObject) return null;

  if (isAddressPanelProperty(targetObject)) {
    return targetObject.streetAddress || targetObject.fullAddress;
  }

  if (isCustomMapGeometry(targetObject)) {
    return targetObject.label;
  }

  return null;
};

type TaskFormStructure = {
  name: string;
  assignees: {
    users: Array<string>;
  };
  status: TASK_STATUS;
  dueDate?: Maybe<string>;
  description?: Maybe<string>;
  targetSubmissionTypeId?: Maybe<string>;
  relatedSubmissionId?: Maybe<string>;
  propertyId?: string;
  customObjectId?: string;
};

const TaskForm = ({
  onSubmit,
  onCancel,
  targetObject,
  existingTask,
  submission,
}: TaskModalHook) => {
  const { account } = React.useContext(AuthContext);
  const { authorized } = React.useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    register,
  } = useForm<TaskFormStructure>({
    defaultValues: existingTask
      ? {
          name: existingTask.name,
          assignees: {
            users: existingTask.assignees.map(assignee => assignee.id),
          },
          status: existingTask.status,
          dueDate: existingTask.dueDate,
          description: existingTask.description,
          targetSubmissionTypeId: existingTask.targetSubmissionType?.id,
          relatedSubmissionId: existingTask.relatedSubmission?.id || null,
        }
      : {
          status: TASK_STATUS.TO_DO,
          targetSubmissionTypeId:
            submission?.submissionTypeVersion?.submissionType?.id || null,
          relatedSubmissionId: submission?.id || null,
          dueDate: null,
        },
  });

  const label = getLabel(targetObject);
  const submitButtonLabel = existingTask ? "Update" : "Save";

  const canEditTask = authorized({
    resource: RESOURCE_NAME.TASK,
    permission: "update",
  });
  const formDisabled = existingTask && !canEditTask ? true : false;
  const taskAssociatedWithSubmission =
    existingTask && existingTask?.relatedSubmission?.id ? true : false;

  const [updateTask] = useUpdateTaskMutation({
    onCompleted: () => {
      addSuccessToast(`Your task at ${label} was updated.`);
      onSubmit();
    },
    onError: _ => {
      addErrorToast(`Your task at ${label} was not updated. Try again.`);
    },
    refetchQueries: [
      GetTasksDocument,
      GetSubmissionDocument,
      GetActivitiesDocument,
      GetAccountActivityDocument,
    ],
  });

  const [createTask, { loading }] = useCreateTaskMutation({
    onCompleted: () => {
      addSuccessToast(`Your task at ${label} was created.`);
      onSubmit();
    },
    onError: _ => {
      addErrorToast(`Your task at ${label} was not created. Try again.`);
    },
    refetchQueries: [
      GetTasksDocument,
      GetSubmissionDocument,
      GetActivitiesDocument,
      GetAccountActivityDocument,
    ],
  });

  const associatedRecordTypeOptions = makeAssociatedRecordTypeOptions({
    submissionTypes: account?.submissionTypes ?? [],
    typedObject: getTypedObject(targetObject),
  });

  const onFormSubmit = async (formData: TaskFormStructure) => {
    if (loading) return;

    // Transform the data to ensure assignees only has users
    const transformedData = {
      ...formData,
      assignees: {
        users: formData.assignees?.users || [],
      },
    };

    if (targetObject) {
      if (isAddressPanelProperty(targetObject)) {
        transformedData.propertyId = targetObject.id;
      }

      if (isCustomMapGeometry(targetObject)) {
        transformedData.customObjectId = targetObject.id;
      }
    }

    if (existingTask) {
      track("Task updated", {
        label,
        propertyId: transformedData.propertyId,
        customObjectId: transformedData.customObjectId,
      });

      await updateTask({
        variables: {
          data: {
            id: existingTask.id,
            ...transformedData,
          },
        },
      });
    } else {
      track("Task created", {
        label,
        propertyId: transformedData.propertyId,
        customObjectId: transformedData.customObjectId,
      });

      await createTask({ variables: { data: transformedData } });
    }
  };

  return (
    <Container
      css={{
        "@media (max-height: 768px) and (min-width: 768px)": {
          maxHeight: "90vh",
        },
      }}
    >
      <HeaderSection>
        <Title size="small" type="semiBold">
          {existingTask ? "Edit task" : "Add new task"}
        </Title>
        {label && (
          <Body type="regular" size="large" color="contentSecondary">
            {label}
          </Body>
        )}
      </HeaderSection>
      <FormSection>
        <ContentSection>
          <InputRow>
            <Text
              {...register("name", {
                required: "Task name is required",
              })}
              error={errors.name?.message}
              label="Task name"
              aria-label="Task name"
              required
              disabled={formDisabled}
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <UserSelect
              control={control}
              name="assignees"
              label="Assignee(s)"
              includeUsers={true}
              includePublicUsers={false}
              disabled={formDisabled}
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <Select
              control={control}
              name="status"
              label="Status"
              disabled={formDisabled}
              options={TASK_STATUS_OPTIONS}
              size="medium"
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <Datepicker
              control={control}
              name="dueDate"
              label="Due date"
              disabled={formDisabled}
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <Textarea
              {...register("description")}
              error={errors.description?.message}
              label="Description"
              disabled={formDisabled}
            />
          </InputRow>
          <InputRow style={{ lineHeight: "1" }}>
            <Select
              control={control}
              name="targetSubmissionTypeId"
              label="Associated record type"
              options={associatedRecordTypeOptions}
              disabled={
                formDisabled || taskAssociatedWithSubmission || !!submission
              }
              size="medium"
              styles={{
                menuPortal: base => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            />
          </InputRow>
        </ContentSection>
      </FormSection>
      <ButtonSection>
        <PrimaryButtons>
          <Button onClick={onCancel} size="medium" styleVariant="secondary">
            Cancel
          </Button>
          <Button
            disabled={loading || formDisabled || !isDirty}
            onClick={handleSubmit(onFormSubmit)}
            size="medium"
            styleVariant="primary"
            type="button"
          >
            {submitButtonLabel}
          </Button>
        </PrimaryButtons>
      </ButtonSection>
    </Container>
  );
};

export default TaskForm;
